import { Schemas } from '~/api/gen'
import { Img } from '~/components/elements/Img'
import { VisibilityIcon } from '~/components/icons/generated/VisibilityIcon'
import { getCoordinateImageAlt } from '~/features/shared/coordinate/lib/getCoordinateImageAlt'
import { viewCountToString } from '~/features/shared/coordinate/lib/viewCountToString'
import { useBinary } from '~/hooks/useBinary'
import { useModalWithTransition } from '~/hooks/usePortalModal/useModalWithTransition'
import { useResizeObserver } from '~/hooks/useResizeObserver'
import { getColorFromColorGroupId } from '~/lib/color'
import { getSeasonFromDate } from '~/lib/dateUtil'
import { sexToGender } from '~/lib/gender/converter'
import { genderToGenderKana } from '~/lib/gender/label'
import { ImageSearch } from '../ImageSearch'
import { CoordinateBalloonButton } from './CoordinateBalloonButton'
import { CoordinateBalloonList } from './CoordinateBalloonList'
import { ImageSearchButton } from './ImageSearchButton'

type Props = {
  coordinate: Schemas.coordinate_large_with_member_shop_brands
  coordinateItems: Schemas.coordinate_item_resource[]
}

// eslint-disable-next-line complexity
export const MainImage: React.FC<Props> = ({ coordinate, coordinateItems }) => {
  const [isBalloonVisible, , , toggleBalloonVisibility] = useBinary(true)
  const [Modal, open, close, isOpen] = useModalWithTransition(300)
  const { ref: mainImageSection, entry } = useResizeObserver()
  const mainImageWidth = entry?.contentRect?.width ?? 0

  const item = coordinateItems[0]
  return (
    <section
      className="relative flex w-full shrink-0 snap-center"
      ref={mainImageSection}
    >
      <h1 className="contents">
        <Img
          className="w-full"
          src={coordinate.image.url_1000}
          alt={`${getCoordinateImageAlt({
            nickName: coordinate.member.nick_name,
            brandName: coordinateItems[0]?.item.brand?.name ?? undefined,
            itemName: coordinateItems[0]?.name,
            categoryName:
              item?.item.item_child_category?.name ??
              item?.item.item_category?.name,
            colorName: getColorFromColorGroupId(item?.color_group?.id ?? 0),
            seasonName: coordinate.published_at
              ? getSeasonFromDate(coordinate.published_at)
              : undefined,
            genderName: genderToGenderKana(sexToGender(coordinate.sex_id)),
            height: coordinate.height ?? undefined,
            bodyFrameTypeName: coordinate.body_frame_type?.name
              ? `骨格${coordinate.body_frame_type?.name}`
              : undefined,
            tagNames: coordinate.tags?.map((tag) => tag.name),
          })}の1枚目の写真`}
          width={1000}
          height={1334}
          onClick={toggleBalloonVisibility}
          priority
          fetchPriority="high"
        />
      </h1>
      <CoordinateBalloonList
        coordinate={coordinate}
        coordinateItems={coordinateItems}
        isBalloonVisible={isBalloonVisible}
        mainImageWidth={mainImageWidth}
      />
      <div className="absolute bottom-4 left-[56px] xl:hidden">
        <ImageSearchButton onClick={open} />
      </div>
      <div className="absolute bottom-4 left-4 xl:bottom-[10px] xl:left-auto xl:right-[10px]">
        <CoordinateBalloonButton
          onClick={toggleBalloonVisibility}
          isBalloonVisible={isBalloonVisible}
        />
      </div>
      <div className="absolute bottom-[10px] left-[10px] hidden items-center gap-1 bg-black/50 px-3 py-1 text-[14px] text-white xl:flex">
        <VisibilityIcon className="h-4" />
        <span className="leading-none">
          {viewCountToString(coordinate.aggregate?.impression_view_count ?? 0)}
        </span>
      </div>
      <Modal
        className={`h-full w-screen overflow-y-auto transition-all duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        overlayClassName={`transition-opacity duration-200 bg-black/40 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <ImageSearch
          coordinateId={coordinate.id}
          imgUrl={coordinate.image.url_1000}
          onCloseClick={close}
        />
      </Modal>
    </section>
  )
}
